import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, doc, updateDoc, getDoc, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import Navigation from '../components/Navigation';
import Pagination from '../components/Pagination';

interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  createdAt?: any;
  lastLogin?: any;
  phoneNumber?: string;
  racerId?: string;
  profileComplete?: boolean;
}

interface RacerData {
  id: string;
  fullName?: string;
  motherName?: string;
  idCardNumber?: string;
  address?: string;
  socialSecurityNumber?: string;
  email?: string;
  phoneNumber?: string;
  userId?: string;
}

const Users: React.FC = () => {
  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [racers, setRacers] = useState<RacerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedRacer, setSelectedRacer] = useState<RacerData | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [userStats, setUserStats] = useState({
    total: 0,
    admins: 0,
    withRacerProfile: 0,
    recentlyActive: 0
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Módosítjuk, hogy állítható legyen

  useEffect(() => {
    if (!currentUser || !isAdmin) {
      navigate('/dashboard');
      return;
    }

    fetchUsers();
    fetchRacers();
  }, [currentUser, isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Felhasználók lekérdezése a 'users' gyűjteményből
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);

      const usersData: UserProfile[] = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        usersData.push({
          id: doc.id,
          email: userData.email || '',
          displayName: userData.displayName || '',
          isAdmin: userData.isAdmin || false,
          createdAt: userData.createdAt,
          lastLogin: userData.lastLogin,
          phoneNumber: userData.phoneNumber || '',
          racerId: userData.racerId || '',
          profileComplete: userData.profileComplete || false
        });
      });

      // console.log('Lekérdezett felhasználók:', usersData.length);
      setUsers(usersData);

      // Statisztikák számítása
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      setUserStats({
        total: usersData.length,
        admins: usersData.filter(user => user.isAdmin).length,
        withRacerProfile: usersData.filter(user => user.racerId).length,
        recentlyActive: usersData.filter(user =>
          user.lastLogin && user.lastLogin.toDate && user.lastLogin.toDate() > thirtyDaysAgo
        ).length
      });

    } catch (error) {
      console.error('Hiba a felhasználók lekérdezése során:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRacers = async () => {
    try {
      const racersRef = collection(db, 'racers');
      const querySnapshot = await getDocs(racersRef);

      const racersData: RacerData[] = [];

      querySnapshot.forEach((doc) => {
        const racerData = doc.data();
        racersData.push({
          id: doc.id,
          fullName: racerData.fullName,
          motherName: racerData.motherName,
          idCardNumber: racerData.idCardNumber,
          address: racerData.address,
          socialSecurityNumber: racerData.socialSecurityNumber,
          email: racerData.email,
          phoneNumber: racerData.phoneNumber,
          userId: racerData.userId
        });
      });

      // console.log('Lekérdezett vendégek:', racersData.length);
      setRacers(racersData);
    } catch (error) {
      console.error('Hiba a vendégek lekérdezése során:', error);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter(user =>
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.phoneNumber && user.phoneNumber.includes(searchTerm))
  );

  // Számoljuk ki a megjelenítendő felhasználókat
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const viewUserDetails = (user: UserProfile) => {
    setSelectedUser(user);

    // Keressük meg a felhasználóhoz tartozó vendég adatokat
    const userRacer = racers.find(racer => racer.userId === user.id || racer.id === user.racerId);
    setSelectedRacer(userRacer || null);

    setShowUserDetails(true);
  };

  const closeUserDetails = () => {
    setShowUserDetails(false);
    setSelectedUser(null);
    setSelectedRacer(null);
  };

  const toggleAdminStatus = async (userId: string, currentStatus: boolean) => {
    if (!currentUser || !isAdmin) return;

    try {
      const userRef = doc(db, 'users', userId);

      await updateDoc(userRef, {
        isAdmin: !currentStatus
      });

      // Frissítjük a felhasználók listáját
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? { ...user, isAdmin: !currentStatus }
            : user
        )
      );

      // Ha a kiválasztott felhasználó admin státuszát változtattuk meg, frissítjük azt is
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({
          ...selectedUser,
          isAdmin: !currentStatus
        });
      }

      setUpdateSuccess(true);
      setUpdateError(null);

      // 3 másodperc múlva eltüntetjük a sikeres frissítés üzenetet
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba az admin státusz módosítása során:', error);
      setUpdateError('Nem sikerült módosítani az admin státuszt. Kérjük, próbáld újra!');

      // 3 másodperc múlva eltüntetjük a hibaüzenetet
      setTimeout(() => {
        setUpdateError(null);
      }, 3000);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Nincs adat';

    const date = timestamp.toDate();
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Hiba a kijelentkezés során:', error);
    }
  };

  const refreshData = () => {
    setLoading(true);
    fetchUsers();
    fetchRacers();
  };

  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900">Felhasználók kezelése</h1>
            <button
              onClick={refreshData}
              className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Frissítés
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Összes felhasználó</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.total}</dd>
                </dl>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Adminisztrátorok</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.admins}</dd>
                </dl>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Vendég profillal</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.withRacerProfile}</dd>
                </dl>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Aktív (30 nap)</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.recentlyActive}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2 sm:mb-0">Felhasználók listája</h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Keresés..."
                    className="border border-gray-300 rounded-md py-2 px-4 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <div className="absolute left-3 top-2.5">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {updateSuccess && (
              <div className="mx-4 my-2 p-2 bg-green-100 text-green-800 rounded-md">
                A felhasználó adatai sikeresen frissítve!
              </div>
            )}

            {updateError && (
              <div className="mx-4 my-2 p-2 bg-red-100 text-red-800 rounded-md">
                {updateError}
              </div>
            )}

            {loading ? (
              <div className="text-center py-4">
                <p className="text-gray-600">Felhasználók betöltése...</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <li key={user.id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center mb-2 sm:mb-0">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            {user.displayName ? (
                              <span className="text-lg font-medium text-gray-700">
                                {user.displayName.charAt(0).toUpperCase()}
                              </span>
                            ) : (
                              <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex flex-wrap items-center">
                              <span className="mr-1">{user.displayName || 'Névtelen felhasználó'}</span>
                              <div className="flex flex-wrap mt-1 sm:mt-0">
                                {user.isAdmin && (
                                  <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                    Admin
                                  </span>
                                )}
                                {user.profileComplete && (
                                  <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Teljes profil
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-end space-x-2">
                          <button
                            onClick={() => viewUserDetails(user)}
                            className="mb-1 px-3 py-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Részletek
                          </button>
                          <button
                            onClick={() => toggleAdminStatus(user.id, user.isAdmin)}
                            className={`mb-1 px-3 py-1 text-xs rounded-md ${user.isAdmin
                              ? 'bg-red-100 text-red-800 hover:bg-red-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                              } font-medium`}
                          >
                            {user.isAdmin ? 'Admin jogok elvétele' : 'Admin jogok adása'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
                {filteredUsers.length === 0 && (
                  <li>
                    <div className="px-4 py-4 text-center text-gray-500">
                      Nincs a keresésnek megfelelő felhasználó.
                    </div>
                  </li>
                )}
              </ul>
            )}

            {/* Pagination hozzáadása */}
            <div className="px-4 py-4 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                list={filteredUsers}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Felhasználó részletek modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-2 border-b">
              <h3 className="text-lg font-medium text-gray-900">Felhasználó részletei</h3>
              <button
                onClick={closeUserDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-2">
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center mb-4">
                  <div className="flex-shrink-0 h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mb-2 sm:mb-0">
                    {selectedUser.displayName ? (
                      <span className="text-2xl font-medium text-gray-700">
                        {selectedUser.displayName.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <svg className="h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <div className="sm:ml-4">
                    <h4 className="text-lg font-medium text-gray-900">
                      {selectedUser.displayName || 'Névtelen felhasználó'}
                    </h4>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                    <div className="mt-1 flex flex-wrap">
                      {selectedUser.isAdmin && (
                        <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Admin
                        </span>
                      )}
                      {selectedUser.profileComplete && (
                        <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Teljes profil
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Felhasználó azonosító:</p>
                    <p className="text-sm font-medium break-all">{selectedUser.id}</p>
                  </div>
                  {selectedUser.phoneNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Telefonszám:</p>
                      <p className="text-sm font-medium">{selectedUser.phoneNumber}</p>
                    </div>
                  )}
                  {selectedUser.racerId && (
                    <div>
                      <p className="text-sm text-gray-500">Vendég azonosító:</p>
                      <p className="text-sm font-medium break-all">{selectedUser.racerId}</p>
                    </div>
                  )}
                  {selectedUser.createdAt && (
                    <div>
                      <p className="text-sm text-gray-500">Regisztráció ideje:</p>
                      <p className="text-sm font-medium">{formatDate(selectedUser.createdAt)}</p>
                    </div>
                  )}
                  {selectedUser.lastLogin && (
                    <div>
                      <p className="text-sm text-gray-500">Utolsó bejelentkezés:</p>
                      <p className="text-sm font-medium">{formatDate(selectedUser.lastLogin)}</p>
                    </div>
                  )}
                </div>
              </div>

              {selectedRacer && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Vendég adatok</h4>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedRacer.fullName && (
                        <div>
                          <p className="text-sm text-gray-500">Teljes név:</p>
                          <p className="text-sm font-medium">{selectedRacer.fullName}</p>
                        </div>
                      )}
                      {selectedRacer.motherName && (
                        <div>
                          <p className="text-sm text-gray-500">Anyja neve:</p>
                          <p className="text-sm font-medium">{selectedRacer.motherName}</p>
                        </div>
                      )}
                      {selectedRacer.idCardNumber && (
                        <div>
                          <p className="text-sm text-gray-500">Személyi igazolvány szám:</p>
                          <p className="text-sm font-medium">{selectedRacer.idCardNumber}</p>
                        </div>
                      )}
                      {selectedRacer.address && (
                        <div>
                          <p className="text-sm text-gray-500">Cím:</p>
                          <p className="text-sm font-medium">{selectedRacer.address}</p>
                        </div>
                      )}
                      {selectedRacer.socialSecurityNumber && (
                        <div>
                          <p className="text-sm text-gray-500">TAJ szám:</p>
                          <p className="text-sm font-medium">{selectedRacer.socialSecurityNumber}</p>
                        </div>
                      )}
                      {selectedRacer.email && (
                        <div>
                          <p className="text-sm text-gray-500">Email:</p>
                          <p className="text-sm font-medium">{selectedRacer.email}</p>
                        </div>
                      )}
                      {selectedRacer.phoneNumber && (
                        <div>
                          <p className="text-sm text-gray-500">Telefonszám:</p>
                          <p className="text-sm font-medium">{selectedRacer.phoneNumber}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-col sm:flex-row sm:justify-between">
                <button
                  onClick={() => toggleAdminStatus(selectedUser.id, selectedUser.isAdmin)}
                  className={`mb-2 sm:mb-0 px-4 py-2 text-sm font-medium rounded-md ${selectedUser.isAdmin
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                >
                  {selectedUser.isAdmin ? 'Admin jogok elvétele' : 'Admin jogok adása'}
                </button>
                <button
                  onClick={closeUserDetails}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>

        </div>

      )}

    </div>
  );
};

export default Users; 